import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Landmark, Plus, Pencil, Trash2, Loader2, Wallet } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { RupiahInput } from "@/components/RupiahInput";
import { formatRupiah, formatDate } from "@/lib/format";

function OpeningBalanceDialog({ company, onClose, onDone }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (company) setItems([
      { account_number: "TUNAI", label: "Tunai Pusat (uang fisik)", opening_balance: company.cash_opening_balance || 0, opening_date: company.cash_opening_date || "" },
      ...(company.accounts || []).map((a) => ({ account_number: a.account_number, label: `${a.bank_name || "Bank"} · ${a.account_number}`, opening_balance: a.opening_balance || 0, opening_date: a.opening_date || "" })),
    ]);
  }, [company]);
  const set = (i, k, v) => setItems((xs) => xs.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/companies/${company.id}/opening-balances`, { items: items.map(({ label, ...x }) => ({ ...x, opening_balance: Number(x.opening_balance) || 0, opening_date: x.opening_date || null })) });
      toast.success("Saldo awal disimpan."); onDone(); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="opening-balance-dialog">
        <DialogHeader><DialogTitle>Saldo Awal Rekening — {company?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Isi saldo sesuai rekening koran bank pada tanggal tertentu. Transaksi sebelum tanggal saldo awal tidak dihitung lagi. Kosongkan tanggal & nominal untuk menghapus saldo awal.</p>
        <form onSubmit={save} className="space-y-3">
          {items.map((x, i) => (
            <div key={x.account_number} className="grid grid-cols-1 sm:grid-cols-[1.2fr_1fr_1fr] gap-2 items-end rounded-md border p-2" data-testid={`opening-row-${x.account_number}`}>
              <p className="font-mono text-sm text-brand pb-2">{x.label}</p>
              <div><Label className="text-xs">Saldo Awal</Label><RupiahInput value={x.opening_balance} onChange={(e) => set(i, "opening_balance", e.target.value)} className="mt-1" data-testid={`opening-amount-${x.account_number}`} /></div>
              <div><Label className="text-xs">Tanggal Saldo Awal</Label><Input type="date" value={x.opening_date} onChange={(e) => set(i, "opening_date", e.target.value)} className="mt-1" data-testid={`opening-date-${x.account_number}`} /></div>
            </div>
          ))}
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="save-opening-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Saldo Awal"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_ACC = { bank_name: "", account_number: "", account_holder: "" };

export default function Companies() {
  const { user } = useAuth();
  const canDelete = user?.role === "it_admin";
  const canEdit = user?.role !== "tim_keuangan";
  const canOpening = ["it_admin", "tim_keuangan"].includes(user?.role);
  const [openingFor, setOpeningFor] = useState(null);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", accounts: [{ ...EMPTY_ACC }] });
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => api.get("/companies").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", accounts: [{ ...EMPTY_ACC }] }); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, accounts: c.accounts?.length ? c.accounts.map((a) => ({ ...EMPTY_ACC, ...a })) : [{ ...EMPTY_ACC }] });
    setOpen(true);
  };
  const setAcc = (i, k, v) => setForm((f) => ({ ...f, accounts: f.accounts.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)) }));
  const addAcc = () => setForm((f) => ({ ...f, accounts: [...f.accounts, { ...EMPTY_ACC }] }));
  const removeAcc = (i) => setForm((f) => ({ ...f, accounts: f.accounts.length > 1 ? f.accounts.filter((_, idx) => idx !== i) : f.accounts }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) await api.put(`/companies/${editing.id}`, form);
      else await api.post("/companies", form);
      toast.success(editing ? "PT diperbarui." : "PT ditambahkan.");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/companies/${delTarget.id}`); toast.success("PT dihapus."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setDelTarget(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Landmark className="h-6 w-6 text-brand" /> Master PT
          </h2>
          <p className="text-sm text-muted-foreground">Kelola data PT & nomor rekening untuk referensi pembayaran tagihan ({rows.length} PT).</p>
        </div>
        {canEdit && <Button onClick={openCreate} className="bg-brand hover:bg-emerald-800" data-testid="add-company-btn">
          <Plus className="h-4 w-4 mr-2" /> Tambah PT
        </Button>}
      </div>

      <Card className="p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama PT</TableHead>
                <TableHead>Rekening</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8" data-testid="companies-empty">Belum ada data PT.</TableCell></TableRow>
              )}
              {rows.map((c) => (
                <TableRow key={c.id} data-testid={`company-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-sm">
                    <div className="space-y-0.5" data-testid={`company-accounts-${c.id}`}>
                      {c.cash_opening_date && <p className="text-xs text-slate-600" data-testid={`company-cash-opening-${c.id}`}><span className="font-medium text-foreground">Tunai Pusat</span> · Saldo awal {formatRupiah(c.cash_opening_balance || 0)} per {formatDate(c.cash_opening_date)}</p>}
                      {(c.accounts || []).map((a, i) => (
                        <p key={i}><span className="font-medium">{a.bank_name || "Bank"}</span> · <span className="font-mono">{a.account_number}</span>{a.account_holder && <span className="text-muted-foreground"> · a.n. {a.account_holder}</span>}{a.opening_date && <span className="text-xs text-slate-600" data-testid={`company-opening-${a.account_number}`}> · Saldo awal {formatRupiah(a.opening_balance || 0)} per {formatDate(a.opening_date)}</span>}</p>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canOpening && <Button size="sm" variant="outline" onClick={() => setOpeningFor(c)} data-testid={`opening-company-${c.id}`}><Wallet className="h-4 w-4 mr-1" /> Saldo Awal</Button>}
                      {canEdit && <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-company-${c.id}`}><Pencil className="h-4 w-4" /></Button>}
                      {canDelete && <Button size="icon" variant="ghost" onClick={() => setDelTarget(c)} className="text-red-600 hover:bg-red-50" data-testid={`delete-company-${c.id}`}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="company-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit PT" : "Tambah PT"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Nama PT</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="company-name-input" placeholder="PT ..." />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Rekening</Label>
                <Button type="button" size="sm" variant="outline" onClick={addAcc} data-testid="company-add-account">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Tambah Rekening
                </Button>
              </div>
              {form.accounts.map((a, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1.2fr_1.2fr_auto] gap-2 items-center rounded-md border p-2" data-testid={`company-account-row-${i}`}>
                  <Input value={a.bank_name} onChange={(e) => setAcc(i, "bank_name", e.target.value)} placeholder="Nama Bank" required data-testid={`company-bank-input-${i}`} />
                  <Input value={a.account_number} onChange={(e) => setAcc(i, "account_number", e.target.value)} placeholder="Nomor Rekening" required className="font-mono" data-testid={`company-account-input-${i}`} />
                  <Input value={a.account_holder} onChange={(e) => setAcc(i, "account_holder", e.target.value)} placeholder="Atas Nama" required data-testid={`company-holder-input-${i}`} />
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeAcc(i)} disabled={form.accounts.length === 1} data-testid={`company-remove-account-${i}`}>
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="save-company-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Simpan" : "Tambah")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <OpeningBalanceDialog company={openingFor} onClose={() => setOpeningFor(null)} onDone={load} />

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus PT {delTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Tagihan lama yang memakai PT ini tetap menyimpan nama & nomor rekeningnya.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-company">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
